import { useState } from "react";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";
import Cart from "../Cart/Cart";
import { useNavigate } from "react-router-dom";

const About = ({ cart, total, removeFromCart }) => {
  const [cartOpen, setCartOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Header
        cartCount={0}
        onCartClick={() => setCartOpen(true)}
        searchTerm=""
        setSearchTerm={() => {}}
        currentPage="about"
        navigate={navigate}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-purple-600 to-pink-600 opacity-90"></div>
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-6xl md:text-7xl font-black text-white mb-6 tracking-tight">
              About <span className="text-yellow-300">GamesHub</span>
            </h1>
            <p className="text-xl md:text-2xl text-red-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              Your ultimate gaming destination where passion meets pixels
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3">
                <span className="text-white font-semibold">
                  🎮 10,000+ Games
                </span>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3">
                <span className="text-white font-semibold">
                  ⭐ 4.9/5 Rating
                </span>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3">
                <span className="text-white font-semibold">🚀 Since 2023</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Story Section */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-black text-gray-900 mb-6">
                Our Story
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Founded in 2023 by a group of passionate gamers, GamesHub was
                born from the frustration of finding quality games at fair
                prices. We believe gaming should be accessible, affordable, and
                most importantly, fun for everyone.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Today, we're proud to serve over 50,000 gamers worldwide,
                offering the latest titles, exclusive deals, and unparalleled
                customer service that keeps our community coming back.
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-red-500 to-purple-600 rounded-2xl p-8 text-white">
                <div className="text-center">
                  <div className="text-6xl mb-4">🎯</div>
                  <h3 className="text-2xl font-bold mb-2">Our Mission</h3>
                  <p className="text-red-100">
                    To create the ultimate gaming ecosystem where every player
                    finds their perfect game
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="mb-20">
          <h2 className="text-4xl font-black text-gray-900 text-center mb-12">
            Why Choose GamesHub?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition duration-300 transform hover:-translate-y-2">
              <div className="text-5xl mb-4">🎮</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Latest Games
              </h3>
              <p className="text-gray-600">
                Access to all new releases on day one with exclusive pre-orders
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition duration-300 transform hover:-translate-y-2">
              <div className="text-5xl mb-4">💰</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Best Prices
              </h3>
              <p className="text-gray-600">
                Competitive pricing with frequent sales and bundle deals
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition duration-300 transform hover:-translate-y-2">
              <div className="text-5xl mb-4">🚚</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Fast Shipping
              </h3>
              <p className="text-gray-600">
                Lightning-fast delivery with free shipping on orders over $50
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition duration-300 transform hover:-translate-y-2">
              <div className="text-5xl mb-4">🛡️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Secure Platform
              </h3>
              <p className="text-gray-600">
                Bank-level security for all transactions and personal data
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition duration-300 transform hover:-translate-y-2">
              <div className="text-5xl mb-4">🎧</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                24/7 Support
              </h3>
              <p className="text-gray-600">
                Round-the-clock customer support from fellow gamers
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition duration-300 transform hover:-translate-y-2">
              <div className="text-5xl mb-4">🏆</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Community
              </h3>
              <p className="text-gray-600">
                Join our gaming community for reviews, tips, and tournaments
              </p>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-12 text-white">
            <h2 className="text-4xl font-black mb-4">Ready to Game?</h2>
            <p className="text-xl mb-8 text-orange-100">
              Join thousands of gamers who trust GamesHub for their gaming needs
            </p>
            <button
              onClick={() => navigate("/products")}
              className="bg-white text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-50 hover:shadow-lg transition duration-300 transform hover:scale-105"
            >
              Start Shopping
            </button>
          </div>
        </section>
      </main>

      <Footer />
      {cartOpen && (
        <Cart
          cart={cart}
          total={total}
          onRemove={removeFromCart}
          onClose={() => setCartOpen(false)}
          onCheckout={() => {
            navigate("/checkout");
            setCartOpen(false);
          }}
        />
      )}
    </div>
  );
};
export default About;
